import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth/types';
import BooleanBadge from 'src/features/membership/components/BooleanBadge';
import BackButton from 'src/features/navigation/components/BackButton';
import { PATH } from 'src/features/navigation/utils/constants';
import { useGetValidatorAccount } from 'src/features/validatorAccounts/api/useGetValidatorAccount';
import DeleteValidatorAccount from 'src/features/validatorAccounts/components/forms/DeleteValidatorAccount';
import UpdateValidatorAccountFormModal from 'src/features/validatorAccounts/components/forms/UpdateValidatorAccountFormModal';
import ValidatorAccountDashboardTables from 'src/features/validatorAccounts/components/ValidatorAccountDashboardTables';
import { validatorAccountsMessages } from 'src/features/validatorAccounts/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import GeneralBlockAttribute from 'src/registry/components/blocks/GeneralBlockAttribute';
import { SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { Button } from 'src/registry/ui/Button';
import { generalMessages } from 'src/registry/utils/messages';
import { useTitleStore } from 'src/stores/useTitleStore';
import { ICON_MAP } from 'src/utils/constants';
import { commonMessages } from 'src/utils/messages';

const ValidatorAccountDashboard = () => {
    const { formatMessage } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();
    const permissionManageValidatorAccount = useHasPermissions([PERMISSION.MANAGE_VALIDATIONS]);

    const { accountId = '' } = useParams();
    const { parkingId = '' } = useParking();

    const { data: validatorAccountData } = useGetValidatorAccount({
        id: accountId,
        parkingId,
        config: { enabled: !!accountId && !!parkingId },
    });

    const isSystemValidatorAccount = Boolean(validatorAccountData?.is_system);

    React.useEffect(() => {
        setTitle(formatMessage(validatorAccountsMessages.validatorAccountDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    return (
        <>
            <BackButton customRoute={`/${PATH.ADMINISTRATION}/validator-accounts`} />
            <div className="flex flex-row items-start justify-between p-0">
                <SectionBlockTitle className="mr-2">{validatorAccountData?.name}</SectionBlockTitle>
                {validatorAccountData && permissionManageValidatorAccount && !isSystemValidatorAccount && (
                    <div className="flex flex-row gap-2">
                        <UpdateValidatorAccountFormModal selectedAccount={validatorAccountData}>
                            <Button size="mdIcon" variant="outline">
                                <div className="block md:hidden">
                                    <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                </div>
                                <span className="hidden md:block">{formatMessage(commonMessages.edit)}</span>
                            </Button>
                        </UpdateValidatorAccountFormModal>
                        <DeleteValidatorAccount selectedAccount={validatorAccountData}>
                            <Button size="mdIcon" variant="destructiveLight">
                                <div className="block md:hidden">
                                    <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                </div>
                                <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                            </Button>
                        </DeleteValidatorAccount>
                    </div>
                )}
            </div>

            <div className="flex flex-col gap-9">
                <div className="flex gap-9">
                    <GeneralBlock className="grid flex-3 grid-cols-2">
                        <GeneralBlockAttribute
                            label={formatMessage(generalMessages.description)}
                            value={validatorAccountData?.description}
                        />
                        <GeneralBlockAttribute label={formatMessage(validatorAccountsMessages.isSystem)}>
                            <BooleanBadge booleanField={validatorAccountData?.is_system} />
                        </GeneralBlockAttribute>
                    </GeneralBlock>
                </div>
            </div>
            <ValidatorAccountDashboardTables validatorAccount={validatorAccountData} />
        </>
    );
};

export default ValidatorAccountDashboard;
